/** @jsxImportSource @emotion/react */
import * as React from "react";
import styled from "@emotion/styled";
import { Global, Theme } from "@emotion/react";
import {
  FieldError,
  RegisterOptions,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  FadeIn,
  Form,
  H1,
  H2,
  H3,
  IconArrow,
  Label,
  Legend,
  Link,
  ProgressBar,
  Radio,
  ScreenReaderText,
  SiteContainer,
  Sparkles,
  Text,
} from "atoms";
import { useUpdateProfile } from "features/profile";
import { Head } from "features/misc";
import { RedirectState } from "types";
import { useAuth } from "features/auth";
import { themePrototype } from "styles/themePrototype";
import { constants } from "config";

const steps = [
  { label: "Intro", component: Intro },
  { label: "Getting Started", component: Start },
  { label: "Reviewing Projects", component: ReviewProjects },
  { label: "Reviewing Artists", component: ReviewArtists },
  { label: "Quiz", component: Quiz },
];

type QuizFormState = {
  dyor: string;
  subjective: string;
  statement: string;
  category: string;
  artist: string;
};

function BecomeAReviewer() {
  // Status of the quiz and step to track progress
  const [isStarted, setIsStarted] = React.useState(false);
  const startQuiz = () => setIsStarted(true);
  const [step, setStep] = React.useState(0);
  const nextStep = step < steps.length - 1 ? () => setStep(step + 1) : null;
  const previousStep = step > 0 ? () => setStep(step - 1) : null;

  // Quiz form
  const { handleSubmit, register, formState } = useForm<QuizFormState>({
    reValidateMode: "onSubmit",
  });

  // Check auth context for user's reviewer status
  const { user } = useAuth();
  const isReviewer = !!user?.roles.includes("curator-level-1");
  const isHolder =
    !!user?.utilities?.find((u) => u === "Hugger") ||
    !!user?.guestPasses?.length;

  const { updateProfile, isLoading } = useUpdateProfile();

  // Update the user's profile which should refresh the Auth context, see above
  const completeReviewerTraining = () => {
    updateProfile({
      ...user,
      completedLevel1Training: true,
    });
  };

  if (!isHolder) {
    return (
      <Wrapper
        css={{
          backgroundColor: themePrototype.colors.yellow,
          textAlign: "center",
        }}
      >
        <Header>
          <H1>
            Become a Hugger
            <Sparkles aria-hidden="true" className="Sparkles" />
          </H1>
        </Header>

        <H2 as="p" size="lg">
          <strong>Thank you for your interest in the HUG!</strong>
        </H2>
        <Text>
          You must be a HUG Pass holder to become an official Hugger and write
          reviews.
        </Text>
        <div>
          <Text>Need a HUG Pass?</Text>
          <SLink href={constants.openseaURL} variant="gradient">
            Buy on OpenSea
          </SLink>
        </div>
      </Wrapper>
    );
  }

  if (isReviewer) {
    // Once the user successfully completes the quiz, they get redirected to the write review form
    // they originally tried to visit before ending up here. But in case the user is already a
    // reviewer and returns to this page, show this message.
    return (
      <Wrapper>
        <Complete />
      </Wrapper>
    );
  }

  if (!isStarted) {
    return (
      <Wrapper
        css={{
          backgroundColor: themePrototype.colors.yellow,
          textAlign: "center",
          paddingTop: "5rem",
        }}
      >
        <Header>
          <H1>
            Become a Hugger
            <Sparkles aria-hidden="true" className="Sparkles" />
          </H1>
        </Header>

        <H2 as="p" size="lg">
          <strong>Thank you for being a part of HUG!</strong>
        </H2>
        <Text>
          You are <em>this</em> close to becoming an official Hugger, where you
          can write reviews to earn HUGs, while also helping your fellow Huggers
          discover more hug-worthy NFT projects and creators!
        </Text>

        <Text>
          <strong>Ready to get started?</strong>
        </Text>

        <Button onClick={startQuiz} variant="gradient">
          Start
        </Button>
      </Wrapper>
    );
  }

  const { component: StepComponent } = steps[step];

  // Quiz is in progress, show progress and the current step
  return (
    <Wrapper>
      <StickyHeader>
        <H1 size="h3">Become a Hugger</H1>
        <div css={{ maxWidth: 720, margin: "0 auto", padding: "10px 0" }}>
          <ProgressBar value={step + 1} max={steps.length + 1}>
            <span>
              <ScreenReaderText>
                Step {step + 1} out of {steps.length}
              </ScreenReaderText>
            </span>
          </ProgressBar>
          <Milestones num={steps.length + 1}>
            {steps.map(({ label }, index) => (
              <li key={label}>
                <strong>{index + 1}</strong>
                <span>{label}</span>
              </li>
            ))}
          </Milestones>
        </div>
      </StickyHeader>

      <SForm onSubmit={handleSubmit(completeReviewerTraining)}>
        {StepComponent && (
          <StepComponent register={register} formState={formState} />
        )}

        <FormButtons>
          {previousStep && (
            <Button
              onClick={() => {
                previousStep();
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
              }}
              arrow={null}
            >
              <IconArrow dir="l" />
              &nbsp;&nbsp;&nbsp;&nbsp;Previous
            </Button>
          )}
          {nextStep && step !== steps.length - 1 && (
            <Button
              onClick={() => {
                nextStep();
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
              }}
              arrow="r"
            >
              Next
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button
              variant="gradient"
              type="submit"
              arrow="r"
              disabled={isLoading}
            >
              Submit
            </Button>
          )}
        </FormButtons>
      </SForm>
    </Wrapper>
  );
}

function Wrapper({ children, ...props }: { children: React.ReactNode }) {
  return (
    <Main {...props}>
      <Head title="Become a Hugger" />

      <Global styles={global} />

      <SSiteContainer>{children}</SSiteContainer>
    </Main>
  );
}

function Intro() {
  return (
    <FadeIn>
      <H2 size="h1">Introduction</H2>

      <Text>
        HUG is currently home to hundreds of NFT projects and artists,
        categorized by things we care about, like founder/artist attributes,
        mission and causes, roadmap categories, and more.
      </Text>

      <Text>
        Our mission is to level the playing field for all creators, while
        helping all of you discover hidden gems from artists you want to collect
        from and unique communities you resonate with and want to be a part of.
      </Text>
    </FadeIn>
  );
}

function Start() {
  return (
    <FadeIn>
      <H2 size="h1">How to Start Reviewing</H2>

      <Text>
        With NFT projects at times being businesses with full-fledged teams and
        products, we like to look at them through a different lens as we do
        standalone artists. Regardless, here are a set of guidelines that apply
        to both!
      </Text>

      <Text as="ul">
        <li>
          Reviews can incorporate both objective and subjective elements.
          Examples of objective elements, including quantitative analysis like
          the number of unique holders, sales and secondary movement, etc.,
          while subjective elements could include things like personal
          anecdotes.
        </li>
        <li>
          Reviews are not financial advice (NFA). Use them as a starting point
          for helping you discover more NFTs you would like to purchase or
          collect.
        </li>
        <li>
          While we hope to make doing your own research (DYOR) something we can
          all do together, here are some tips for how you can get started on
          your own:
          <Text as="ul">
            <li>
              <strong>Avoid the fear of missing out (FOMO):</strong>
              <br />
              Slow down and make decisions on your own terms instead of others.
            </li>
            <li>
              <strong>Be patient:</strong>
              <br />
              Take as much time as you need to understand a project or go
              through an artist&apos;s work to see if it resonates with you.
            </li>
            <li>
              <strong>Stay vigilant:</strong>
              <br />
              There are many fake servers, listings, or websites that are made
              to look like the real thing. Always make sure you are visiting
              official links provided by a project and/or artist.
            </li>
            <li>
              <strong>Ask questions:</strong>
              <br />
              Where you can, pose questions to a project&apos;s existing
              community or an artist&apos;s existing collectors! After all, we
              all have our own personal criteria, and projects/artists may
              resonate differently with different people.
            </li>
          </Text>
        </li>
      </Text>
    </FadeIn>
  );
}

function ReviewProjects() {
  return (
    <FadeIn>
      <H2 size="h1">Reviewing Projects</H2>

      <Text>
        As mentioned, NFT projects can sometimes be NFT-enabled businesses. They
        can also be an effective way to raise awareness and funding for social
        impact causes. As such, here are some categories you can use to review
        or assess NFT projects critically.
      </Text>

      <H3>Founders/Team</H3>
      <Text as="ul">
        <li>Who is the founding team behind the project?</li>
        <li>
          Is the team doxxed? Regardless if they are, do they have a proven
          track record of accomplishing their business goals or objectives?
        </li>
        <li>How experienced are they?</li>
      </Text>

      <H3>Mission and Cause</H3>
      <Text as="ul">
        <li>What is the project&apos;s mission or cause?</li>
        <li>
          Does it resonate with you? If not, do you see this mission or cause
          resonating with others?
        </li>
        <li>
          Is the project&apos;s mission/cause philanthropic or for profit? A
          project that is geared towards social impact may need to be looked at
          differently versus one that is for profit.
        </li>
      </Text>

      <H3>Business Plan or Roadmap</H3>
      <Text as="ul">
        <li>
          Does the project have a viable roadmap or business plan that
          contributes to their overarching goals?
        </li>
        <li>
          Do you think that the roadmap is realistic and do you think it is
          sufficiently differentiated from everything else you are seeing in the
          market?
        </li>
      </Text>

      <H3>Art</H3>
      <Text as="ul">
        <li>
          We get that art is subjective, but does it spark joy? Alternatively,
          do you think that it is unique enough and can stand on its own outside
          of being associated with the project?
        </li>
      </Text>

      <H3>Community</H3>
      <Text as="ul">
        <li>
          What is your take on the health of the community? Sometimes the size
          of the community may matter less than how active and engaged it is.
        </li>
        <li>
          If you were to pop into the community&apos;s Discord and ask what this
          project is about, what kind of response would you receive?
        </li>
      </Text>
    </FadeIn>
  );
}

function ReviewArtists() {
  return (
    <FadeIn>
      <H2 size="h1">Reviewing Artists</H2>

      <Text>
        Unlike projects, collecting from artists should not come with the
        expectation of a roadmap or business deliverables. Instead, we encourage
        you to ask yourself more personal questions to see if a certain artist
        resonates with you. Here are some examples!
      </Text>

      <Text as="ul">
        <li>Does this artist&apos;s art spark joy?</li>
        <li>
          Have you gotten to know the artist? What about their story and style
          inspires you?
        </li>
        <li>What kind of art style are you drawn to?</li>
        <li>
          Have you thought about diversifying your art collection, not just in
          terms of style, but also in terms of collecting from underrepresented
          artists?
        </li>
      </Text>
    </FadeIn>
  );
}

function Complete() {
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation = (location.state as RedirectState)?.from;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <FadeIn css={{ textAlign: "center", paddingTop: "5rem" }}>
      <Header>
        <H2 size="h1">
          Congratulations!
          <Sparkles aria-hidden="true" className="Sparkles" />
        </H2>
      </Header>

      <Text size="md">
        <strong>
          You have successfully completed our onboarding
          <br /> and are officially a Hugger!
        </strong>
      </Text>
      <Text>
        You have been rewarded with 250 HUG Points and can earn further
        by&nbsp;reviewing projects&nbsp;and&nbsp;artists.
      </Text>

      {previousLocation ? (
        <Button variant="gradient" onClick={() => navigate(previousLocation)}>
          Continue your review
        </Button>
      ) : (
        <Button variant="gradient" onClick={() => navigate("/search/")}>
          Explore to start reviewing
        </Button>
      )}
    </FadeIn>
  );
}
type QuizProps = Pick<UseFormReturn<QuizFormState>, "register" | "formState">;

function Quiz({ register, formState }: QuizProps) {
  const options = {
    required: true,
    validate: (value: string) => value === "right",
  } as RegisterOptions;

  const { errors } = formState;
  const errorMessage = (error?: FieldError) =>
    error?.type === "required"
      ? "Please choose an answer"
      : "This answer is incorrect";

  return (
    <FadeIn>
      <H2 size="h1">Quiz</H2>
      <Text>
        Let&apos;s recap what we have learned so far through a quick quiz!
      </Text>

      <Fieldset>
        <Legend>What does DYOR stand for?</Legend>
        <Radio
          {...register("dyor", options)}
          value="right"
          label="Do Your Own Research"
        />
        <Radio
          {...register("dyor", options)}
          value="wrong1"
          label="Design Your Own Rug"
        />
        <Radio
          {...register("dyor", options)}
          value="wrong2"
          label="Draft Your Own Rules"
        />

        {errors.dyor && (
          <ErrorText>
            <IconArrow dir="u" />
            &nbsp;&nbsp;{errorMessage(errors.dyor)}
          </ErrorText>
        )}
      </Fieldset>

      <Fieldset>
        <Legend>
          A review can incorporate both subjective and objective elements.
        </Legend>
        <Radio
          {...register("subjective", options)}
          value="right"
          label="True"
        />
        <Radio
          {...register("subjective", options)}
          value="wrong"
          label="False"
        />

        {errors.subjective && (
          <ErrorText>
            <IconArrow dir="u" />
            &nbsp;&nbsp;{errorMessage(errors.subjective)}
          </ErrorText>
        )}
      </Fieldset>

      <Fieldset>
        <Legend>Which one of the following statements is true?</Legend>
        <Radio
          {...register("statement", options)}
          value="wrong1"
          label="Reviews inform which NFTs make a good investment, and which do not."
        />
        <Radio
          {...register("statement", options)}
          value="right"
          label="Reviews are not financial advice."
        />
        <Radio
          {...register("statement", options)}
          value="wrong2"
          label="Reviews act as a single source of truth for NFT projects."
        />

        {errors.statement && (
          <ErrorText>
            <IconArrow dir="u" />
            &nbsp;&nbsp;{errorMessage(errors.statement)}
          </ErrorText>
        )}
      </Fieldset>

      <Fieldset>
        <Legend>
          Which of the following categories can be used to review or assess NFT
          projects?
        </Legend>
        <Radio
          {...register("category", options)}
          value="wrong1"
          label="Founder/team"
        />
        <Radio
          {...register("category", options)}
          value="wrong2"
          label="Mission/cause"
        />
        <Radio
          {...register("category", options)}
          value="wrong3"
          label="Business/roadmap"
        />
        <Radio {...register("category", options)} value="wrong4" label="Art" />
        <Radio
          {...register("category", options)}
          value="right"
          label="All of the above"
        />

        {errors.category && (
          <ErrorText>
            <IconArrow dir="u" />
            &nbsp;&nbsp;{errorMessage(errors.category)}
          </ErrorText>
        )}
      </Fieldset>

      <Fieldset>
        <Legend>
          Collecting from artists should not come with the expectation of a
          business roadmap.
        </Legend>
        <Radio {...register("artist", options)} value="right" label="True" />
        <Radio {...register("artist", options)} value="wrong" label="False" />

        {errors.artist && (
          <ErrorText>
            <IconArrow dir="u" />
            &nbsp;&nbsp;{errorMessage(errors.artist)}
          </ErrorText>
        )}
      </Fieldset>
    </FadeIn>
  );
}

const Main = styled.main(({ theme }) => ({
  background: `radial-gradient(circle farthest-corner at top center, ${theme.colors.pink} 5rem, rgba(255, 255, 255, 0) 30rem)`,
  overflow: "visible",
  "p+button": {
    marginTop: 20,
  },
}));

const SSiteContainer = styled(SiteContainer)({
  paddingTop: "0px !important",
});

const Header = styled.header(({ theme }) => ({
  marginLeft: "-1rem",
  marginRight: "-1rem",
  padding: "0 0 3rem",
  position: "relative",
  textAlign: "center",
  "h1, h2": {
    margin: "0 auto",
    maxWidth: 660,
    padding: "0 1em",
    position: "relative",
  },
  ".Sparkles": {
    backgroundSize: "min(60px, 15%)",
    bottom: 0,
    left: "-5%",
    minHeight: 70,
    position: "absolute",
    right: 0,
    top: 0,
  },
}));
const StickyHeader = styled(Header)(({ theme }) => ({
  backgroundColor: themePrototype.colors.yellow,
  boxShadow: `-20vw 0 0 0 ${theme.colors.yellow},-40vw 0 0 0 ${theme.colors.yellow}, 20vw 0 0 0 ${theme.colors.yellow}, 40vw 0 0 0 ${theme.colors.yellow}`,
  height: 100,
  marginBottom: "clamp(100px, 12vw, 110px)",
  padding: ".75rem 1rem 1rem",
  position: "sticky",
  top: "60px",
  zIndex: 10,
  "&>*": {
    position: "relative",
    zIndex: 3,
  },
  "@media (min-width: 600px)": {
    height: 130,
  },
  "@media (min-width: 831px)": {
    top: 75,
  },
  "&::after": {
    content: "''",
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: "100vw",
    height: ".25rem",
    borderRadius: "0 0 45% 45%",
    boxShadow: theme.boxShadow.dark,
    transform: "translateX(-50%)",
    zIndex: "-1",
  },
  "&::before": {
    backgroundColor: "inherit",
    content: "''",
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: "100vw",
    height: "1rem",
    transform: "translateX(-50%)",
    zIndex: "1",
  },
}));
const SForm = styled(Form)({
  gap: "2rem",
  maxWidth: 800,
  marginLeft: "auto",
  marginRight: "auto",
});
const FormButtons = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
});
const ErrorText = styled(Text)(({ theme }) => ({
  color: "#cc3e00",
}));

type WithNum = { num: number };

const Milestones = styled.ul<WithNum>(({ theme, num }) => ({
  display: "flex",
  listStyleType: "none",
  margin: 0,
  padding: 0,
  strong: {
    display: "block",
  },
  li: {
    flex: `0 0 calc(100% / ${num})`,
    maxWidth: `calc(100% / ${num})`,
    fontSize: 12,
    margin: 0,
    transform: "translate(50%, 0)",
    padding: "5px 2px",
    position: "relative",
    span: {
      display: "block",
      fontSize: 11,
      lineHeight: "1.1",
    },
  },
  "li::before": {
    content: '"\\200B"',
    position: "absolute",
  },
  "li::after": {
    backgroundColor: theme.colors.fg,
    borderRadius: theme.borderRadius.round,
    bottom: "calc(100% + 4px)",
    content: "''",
    height: "12px",
    left: "50%",
    opacity: ".4",
    position: "absolute",
    transform: "translateX(-50%)",
    width: "2px",
  },
  "@media (max-width: 600px)": {
    "li span": {
      display: "none",
    },
  },
  "@media (min-width: 831px)": {
    "li span": {
      fontSize: 13,
    },
  },
}));

const global = (theme: Theme) => ({
  "html,body": {
    // background: theme.colors.yellow,
  },
});

const Fieldset = styled(Label)({
  marginTop: "2rem",
  "&:first-of-type": {
    marginTop: "4rem",
  },
}).withComponent("fieldset");

const SLink = styled(Link)({
  justifyContent: "center",
  whiteSpace: "nowrap",
  svg: {
    marginLeft: "0.8rem",
    height: "26px",
  },
});

export { BecomeAReviewer };
